import { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

type ToastItem = {
  id: number
  message: string
  type?: 'info' | 'success' | 'error' | 'brand'
  duration?: number
  closing?: boolean
}

type ToastContextValue = {
  showToast: (message: string, opts?: { type?: ToastItem['type']; duration?: number }) => void
  showError: (message: string, duration?: number) => void
  showSuccess: (message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within <ToastProvider/>')
  return ctx
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const seq = useRef(1)
  const timers = useRef<Map<number, number>>(new Map())

  const remove = useCallback((id: number) => {
    const t = timers.current.get(id)
    if (t) { window.clearTimeout(t); timers.current.delete(id) }
    setToasts(items => items.filter(to => to.id !== id))
  }, [])

  const beginClose = useCallback((id: number) => {
    setToasts(items => items.map(t => (t.id === id ? { ...t, closing: true } : t)))
    const timeout = window.setTimeout(() => remove(id), 220)
    timers.current.set(id, timeout)
  }, [remove])

  const showToast = useCallback((message: string, opts?: { type?: ToastItem['type']; duration?: number }) => {
    const id = seq.current++
    const t: ToastItem = { id, message, type: opts?.type || 'info', duration: opts?.duration ?? 3000 }
    setToasts(items => [...items, t])
    const timeout = window.setTimeout(() => beginClose(id), t.duration)
    timers.current.set(id, timeout)
  }, [beginClose])

  const value = useMemo<ToastContextValue>(() => ({
    showToast,
    showError: (msg, duration) => showToast(msg, { type: 'error', duration }),
    showSuccess: (msg, duration) => showToast(msg, { type: 'success', duration })
  }), [showToast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', top: 16, zIndex: 3000, display: 'grid', gap: 10, maxWidth: 'min(92vw, 520px)' }}>
          {toasts.map(t => (
              <div key={t.id}
                 role={t.type === 'error' ? 'alert' : 'status'}
                 aria-live={t.type === 'error' ? 'assertive' : 'polite'}
                 style={{
                   display: 'grid',
                   gridTemplateColumns: 'auto 1fr auto',
                   alignItems: 'center',
                   gap: 10,
                   padding: '.8rem 1rem',
                   borderRadius: 12,
                   color: (t.type === 'error' || t.type === 'brand') ? '#fff' : (t.type === 'success') ? '#064e3b' : 'inherit',
                   background: (t.type === 'error') ? '#ef4444' : (t.type === 'success') ? '#d1fae5' : (t.type === 'brand') ? '#2563eb' : 'var(--card, #fff)',
                   border: (t.type === 'error' || t.type === 'brand') ? 'none' : '1px solid var(--border, #e5e7eb)',
                   boxShadow: '0 12px 30px rgba(0,0,0,.18)',
                   transform: t.closing ? 'translateY(-10px)' : 'translateY(0px)',
                   opacity: t.closing ? 0 : 1,
                   transition: 'transform .2s ease, opacity .2s ease'
                 }}
            >
              <span aria-hidden style={{ display: 'inline-flex' }}>
                {t.type === 'error' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                )}
                {t.type === 'success' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                )}
                {t.type === 'brand' && (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="8"/></svg>
                )}
                {!t.type || t.type === 'info' ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="8"/></svg>
                ) : null}
              </span>
              <div style={{ fontWeight: 600, lineHeight: 1.25 }}>{t.message}</div>
              <button
                aria-label="Закрыть уведомление"
                onClick={() => beginClose(t.id)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  color: 'inherit',
                  padding: 4,
                  cursor: 'pointer'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  )
}
